// Imports
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import Select from '../../../../../../shareables/foundation/front-end/components/forms/fields/select';
import Textarea from '../../../../../../shareables/foundation/front-end/components/forms/fields/textarea';
import { useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../../../shareables/integrations/api/components/form';
import { PostMessageParameters } from '../../../../../../shareables/types/api/main/website/page/type/219/message/post';
import removePersistedFormEntry from '../../../../../../utils/remove-persisted-form-entry';
import { GeneratedThemeProps } from '../../../../../templates/template-style';
import SignInAnchor from '../../../../sign-in-anchor';
import FormSubmitted from '../shared-components/form-submitted';


// Styled components
const Container = styled.div<{ theme: GeneratedThemeProps }>`
	line-height: 1.3125rem;
`;


// Define the accepted props
interface Props {
	pageID: number;
	storybookSubmitted?: boolean;
}


// Function component
const ResidentAlerts: React.FC<Props> = ({ pageID, storybookSubmitted }) => {
	// Use state
	const [formSubmitted, setFormSubmitted] = useState(storybookSubmitted ?? false);
	
	
	// Use Redux functionality
	const user = useAppSelector((state) => state.common.user);
	const member = useAppSelector((state) => state.self.member);
	
	
	// Use React Router functionality
	const location = useLocation();
	
	
	// Reset form submission state on location change
	//  -> Allows the user to see the form after submission, when clicking link to page they're already on
	useEffect(() => {
		if (!(window as typeof window & { STORYBOOK?: true }).STORYBOOK) {
			setFormSubmitted(false);
		}
	}, [location]);
	
	
	// Build form options
	const formOptions: APIFormOptions<PostMessageParameters, undefined, undefined> = {
		action: 'POST',
		uri: `/website/${window.location.hostname}/page/id_${pageID}/type/219/message`,
		submitButton: {
			text: 'Send alert to all community members',
			clipLongContent: true,
		},
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		// Reset form
		removePersistedFormEntry(`POST /website/${window.location.hostname}/page/id_${pageID}/type/219/message`);
		
		
		// Display success
		setFormSubmitted(true);
	};
	
	
	// Return JSX
	return user.signedIn ? (
		<Container>
			{formSubmitted ? (
				<FormSubmitted
					header='Form submitted!'
					body='You’ve successfully submitted this alert form. Members of your community will receive this message via email shortly.'
				/>
			) : (
				<APIForm options={formOptions} onCompletion={handleFormCompletion}>
					<Select name='identifier' label='Your name' initialValue='MEMBER' required>
						<option value='MEMBER'>{member?.names.member}</option>
						{member?.names.member !== member?.names.account && <option value='ACCOUNT'>{member?.names.account}</option>}
						<option value='FAMILY'>{member?.names.family}</option>
					</Select>
					<Textarea
						label='Alert message'
						name='message'
						note='Use for alerting other residents about emergencies, public service announcements, etc.'
						required
						rows={14}
						maxLength={1000}
						displayCharacterCount
					/>
				</APIForm>
			)}
		</Container>
	) : (
		<em>
			Please <SignInAnchor>sign in</SignInAnchor> before sending an alert.
		</em>
	);
};

export default ResidentAlerts;
